import { BookOpen, KeyRound, LifeBuoy, LogOut, Menu, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router';
import { deconnexion, useSession } from '@/auth/session';
import { useEntreesNavigation } from '@/components/layout/navigation';
import { ClocheNotifications } from '@/components/notifications';
import { Button } from '@/components/ui/button';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

/** Préférence d'affichage de la barre latérale sur grand écran, propre au navigateur de l'utilisateur. */
const CLE_BARRE_LATERALE = 'fonarev360.barreLaterale';
/** Seuil « grand écran » : au-dessus, la barre latérale peut rester ancrée à gauche (point de rupture `lg` de Tailwind). */
const GRAND_ECRAN = '(min-width: 1024px)';

function lireBarreLaterale(): boolean {
  try {
    return localStorage.getItem(CLE_BARRE_LATERALE) !== 'masquee';
  } catch {
    return true;
  }
}

function memoriserBarreLaterale(visible: boolean) {
  try {
    localStorage.setItem(CLE_BARRE_LATERALE, visible ? 'visible' : 'masquee');
  } catch {
    // Stockage indisponible (navigation privée) : la préférence vaut pour la session en cours seulement.
  }
}

/** Vrai quand la fenêtre est assez large pour ancrer la barre latérale ; suit les redimensionnements. */
function useGrandEcran(): boolean {
  const [grand, setGrand] = useState(() => typeof window !== 'undefined' && window.matchMedia(GRAND_ECRAN).matches);
  useEffect(() => {
    const requete = window.matchMedia(GRAND_ECRAN);
    const suivre = (e: MediaQueryListEvent) => setGrand(e.matches);
    requete.addEventListener('change', suivre);
    setGrand(requete.matches);
    return () => requete.removeEventListener('change', suivre);
  }, []);
  return grand;
}

function Navigation({ onClick }: { onClick?: () => void }) {
  const session = useSession();
  const profils = session?.utilisateur.profils ?? [];
  const entrees = useEntreesNavigation();
  return (
    <nav className="flex flex-col gap-1 p-3">
      {entrees.map(({ a, l, I, badge }) => (
        <NavLink
          key={a}
          to={a}
          onClick={onClick}
          className={({ isActive }) =>
            cn('flex items-center gap-3 rounded-md px-3 py-2 text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground', isActive && 'bg-sidebar-accent text-sidebar-accent-foreground font-medium')
          }
        >
          <I className="size-4" />
          <span className="flex-1">{l}</span>
          {(badge ?? 0) > 0 && <span className="rounded-full bg-sidebar-primary px-2 py-0.5 text-xs font-semibold text-sidebar-primary-foreground">{badge}</span>}
        </NavLink>
      ))}
      {/* Guide utilisateur (livrable du cahier des charges) : page HTML servie avec l'application, Word téléchargeable depuis la page. */}
      <a href="/aide/guide-utilisateur.html" target="_blank" rel="noreferrer" onClick={onClick} className="mt-2 flex items-center gap-3 rounded-md border-t border-sidebar-border px-3 pt-3 pb-2 text-sm text-sidebar-foreground/70 hover:text-sidebar-accent-foreground">
        <LifeBuoy className="size-4" />
        <span className="flex-1">Aide · guide utilisateur</span>
      </a>
      {(profils.includes('ADMINISTRATEUR') || profils.includes('DIRECTEUR_GENERAL') || profils.includes('DIRECTEUR_FINANCIER')) && (
        <a href="/aide/index.html" target="_blank" rel="noreferrer" onClick={onClick} className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-sidebar-foreground/70 hover:text-sidebar-accent-foreground">
          <BookOpen className="size-4" />
          <span className="flex-1">Documentation technique</span>
        </a>
      )}
    </nav>
  );
}

function Marque() {
  return (
    <div className="flex items-center gap-3 px-4 py-4">
      <img src="/logo.png" alt="FONAREV" className="size-9 rounded bg-white object-contain p-1" />
      <div>
        <div className="text-sm font-semibold leading-tight text-sidebar-foreground">FONAREV 360</div>
        <div className="text-xs text-sidebar-foreground/60">Version de test</div>
      </div>
    </div>
  );
}

/**
 * Cadre de l'application : barre latérale ancrée sur grand écran (masquable, préférence mémorisée) ou ouverte en tiroir
 * sur mobile et quand elle est masquée ; un même bouton de bascule à toutes les tailles ; en-tête avec l'utilisateur ;
 * contenu des pages.
 */
export function AppShell() {
  const session = useSession();
  const navigate = useNavigate();
  const grand = useGrandEcran();
  const [ouvert, setOuvert] = useState(false);
  const [barre, setBarre] = useState(lireBarreLaterale);
  const u = session?.utilisateur;

  // Sur grand écran, la bascule ancre ou masque la barre ; sinon elle ouvre ou ferme le tiroir.
  const barreAffichee = grand ? barre : ouvert;
  const basculer = () => {
    if (grand) {
      const visible = !barre;
      setBarre(visible);
      memoriserBarreLaterale(visible);
    } else {
      setOuvert(!ouvert);
    }
  };
  const libelleBascule = barreAffichee ? 'Masquer la barre latérale' : 'Afficher la barre latérale';

  const sortir = async () => {
    await deconnexion();
    navigate('/connexion', { replace: true });
  };

  return (
    <div className="flex min-h-screen">
      <aside className={cn('hidden w-64 shrink-0 flex-col bg-sidebar', barre && 'lg:flex')} data-testid="barre-laterale">
        <Marque />
        <Navigation />
      </aside>
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 items-center gap-2 border-b bg-card px-4 sm:gap-3">
          <Button variant="ghost" size="icon" onClick={basculer} aria-pressed={barreAffichee} aria-label={libelleBascule} title={libelleBascule} data-testid="bascule-barre">
            {barreAffichee ? <PanelLeftClose className="size-5" /> : <PanelLeftOpen className="size-5" />}
          </Button>
          <Sheet open={ouvert} onOpenChange={setOuvert}>
            {/* Sur grand écran, barre masquée : le menu reste consultable en tiroir sans la réafficher. */}
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className={cn('hidden', !barre && 'lg:inline-flex')} aria-label="Menu" title="Menu">
                <Menu className="size-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-72 bg-sidebar p-0 text-sidebar-foreground" showCloseButton={false}>
              <SheetTitle className="sr-only">Navigation</SheetTitle>
              <div className="flex items-center justify-between pr-2">
                <Marque />
                <Button variant="ghost" size="icon" onClick={() => setOuvert(false)} aria-label="Masquer la barre latérale" title="Masquer la barre latérale" className="text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground">
                  <PanelLeftClose className="size-5" />
                </Button>
              </div>
              <Navigation onClick={() => setOuvert(false)} />
            </SheetContent>
          </Sheet>
          {!barre && (
            <Link to="/tableau-de-bord" className="hidden items-center gap-2 rounded-md px-1 py-1 hover:bg-accent lg:flex" title="Tableau de bord">
              <img src="/logo.png" alt="" className="size-7 rounded object-contain" />
              <span className="text-sm font-semibold">FONAREV 360</span>
            </Link>
          )}
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">
              {u?.prenom} {u?.nom}
            </div>
            <div className="truncate text-xs text-muted-foreground">
              {u?.fonction} · {u?.structureCode} · {u?.source === 'ENTRA' ? 'Microsoft 365' : 'compte local'}
            </div>
          </div>
          {u?.source === 'LOCAL' && (
            <Button variant="ghost" size="icon" asChild aria-label="Mon mot de passe" title="Mon mot de passe">
              <Link to="/mot-de-passe">
                <KeyRound className="size-5" />
              </Link>
            </Button>
          )}
          <ClocheNotifications />
          <Button variant="outline" size="sm" onClick={sortir}>
            <LogOut className="size-4" />
            <span className="hidden sm:inline">Déconnexion</span>
          </Button>
        </header>
        <main className="flex-1 px-4 py-5 sm:px-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
